import { classifyProjectFileUpload } from "../utils/projectFileUploadClassificationModel";

function safeStr(value) {
  return (value || "").toString();
}

function lower(value) {
  return safeStr(value).toLowerCase();
}

function unique(values) {
  return Array.from(new Set((values || []).filter(Boolean)));
}

function isImageContentType(type) {
  return lower(type).startsWith("image/");
}

function isImageName(name) {
  return /\.(png|jpe?g|gif|webp|bmp|heic|heif)$/i.test(safeStr(name));
}

function isPdfName(name) {
  return lower(name).endsWith(".pdf");
}

function manualOverride(value) {
  const normalized = lower(value);
  return normalized && normalized !== "auto" ? normalized : "";
}

const EXPLICIT_LIBRARY_TYPES = Object.freeze(["client_document", "controlled_document"]);

function inferLibraryType(file) {
  const stored = lower(file?.libraryType);
  if (EXPLICIT_LIBRARY_TYPES.includes(stored)) return stored;

  const classified = classifyProjectFileUpload({
    fileName: file?.name,
    mimeType: file?.contentType || file?.type,
    selectedLibraryType: file?.manualLibraryType || file?.libraryTypeManual || "auto",
    uploadContext: [file?.category, file?.subcategory, file?.deliverableType].filter(Boolean).join(" "),
    metadata: file,
  });
  return classified.libraryType || "document";
}

function inferDrawingStage(file) {
  const text = [file?.name, file?.subcategory, file?.phase].map(lower).join(" ");
  if (text.includes("concept")) return "concept";
  if (text.includes("existing")) return "existing";
  if (text.includes("development")) return "development";
  if (text.includes("permit")) return "permit";
  if (text.includes("construction") || text.includes("issued for construction")) return "construction";
  if (text.includes("as-built") || text.includes("record")) return "as_built";
  return "unspecified";
}

function inferDiscipline(file) {
  const manual = manualOverride(file?.manualLibraryDiscipline || file?.libraryDisciplineManual);
  if (manual) return manual;

  const text = [file?.name, file?.subcategory, file?.category, ...(Array.isArray(file?.matchTags) ? file.matchTags : [])].map(lower).join(" ");
  if (/architect|floor plan|elevation|section|detail|site plan|roof plan|door|window/.test(text)) return "architectural";
  if (/structural|foundation|beam|joist|framing/.test(text)) return "structural";
  if (/mechanical|hvac|ventilation|duct/.test(text)) return "mechanical";
  if (/electrical|lighting|panel|power/.test(text)) return "electrical";
  if (/plumbing|drain|sanitary|water/.test(text)) return "plumbing";
  if (/civil|grading|survey|servicing/.test(text)) return "civil";
  if (/landscape|site works|planting/.test(text)) return "landscape";
  return "general";
}

function inferSheetCode(fileName) {
  const name = safeStr(fileName || "");
  const match = name.match(/\b([A-Z]{1,3}[\-\.]?\d{1,3}(?:\.\d{1,2})?)\b/);
  return match ? match[1].replace(/\./g, "-") : "";
}

function inferRevision(fileName) {
  const name = safeStr(fileName || "");
  const patterns = [
    /\brev(?:ision)?[\s_-]*([A-Z0-9]{1,3})\b/i,
    /\bver(?:sion)?[\s_-]*([A-Z0-9]{1,3})\b/i,
    /\bv([0-9]{1,2})\b/i,
    /\br([0-9]{1,2})\b/i,
  ];
  for (const pattern of patterns) {
    const match = name.match(pattern);
    if (match) return match[1].toUpperCase();
  }
  return "";
}

function inferDocumentClass(file) {
  const text = [file?.name, file?.category, file?.subcategory].map(lower).join(" ");
  if (/spec|manual/.test(text)) return "specification";
  if (/contract|agreement|proposal/.test(text)) return "contract";
  if (/report|analysis|study|memo/.test(text)) return "report";
  if (/permit|application|carl|submission|form/.test(text)) return "permit_doc";
  if (/budget|estimate|cost/.test(text)) return "financial";
  if (/schedule|timeline|gant/.test(text)) return "schedule";
  if (/meeting|minutes|client|decision|correspondence/.test(text)) return "correspondence";
  return "general";
}


function normalizeIndexText(value) {
  return lower(value)
    .replace(/[_]+/g, ' ')
    .replace(/[^a-z0-9\-\.\s]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function uniqueTokens(values) {
  return unique(
    (Array.isArray(values) ? values : [])
      .flatMap((value) => normalizeIndexText(value).split(/\s+/))
      .map((token) => safeStr(token).trim())
      .filter((token) => token.length >= 2)
  );
}

function stripFileExtension(name) {
  return safeStr(name || '').replace(/\.[a-z0-9]{2,6}$/i, '');
}

function deriveDrawingTitle(file) {
  const raw = stripFileExtension(file?.name || '');
  const noSheet = raw.replace(/^\s*[A-Z]{1,3}[\-\.]?\d{1,3}(?:[\-\.]\d{1,2})?\s*[-_:–—]?\s*/i, '');
  return noSheet.replace(/[_]+/g, ' ').replace(/\s+/g, ' ').trim();
}

function buildLibraryContentIndex(file, metadata = {}) {
  const extracted = [
    file?.contentIndexText,
    file?.extractedText,
    file?.pdfText,
    file?.ocrText,
    file?.textContent,
    file?.searchText,
  ]
    .map((value) => safeStr(value || ''))
    .filter(Boolean)
    .join(' ');

  const title = deriveDrawingTitle(file);
  const inputs = [
    safeStr(file?.name),
    title,
    safeStr(metadata?.sheetCode || file?.sheetCode),
    safeStr(file?.deliverableType),
    safeStr(file?.category),
    safeStr(file?.subcategory),
    safeStr(file?.phase),
    safeStr(metadata?.libraryType || file?.libraryType),
    safeStr(metadata?.libraryStage || file?.libraryStage),
    safeStr(metadata?.libraryDiscipline || file?.libraryDiscipline),
    safeStr(metadata?.documentClass || file?.documentClass),
    safeStr(file?.manualLibraryType),
    safeStr(file?.manualLibraryDiscipline),
    ...(Array.isArray(file?.matchTags) ? file.matchTags : []),
    ...(Array.isArray(file?.contentTags) ? file.contentTags : []),
    extracted,
  ].filter(Boolean);

  const text = normalizeIndexText(inputs.join(' '));
  return {
    contentIndexTitle: title,
    contentIndexText: text,
    contentIndexTokens: uniqueTokens(inputs),
    contentIndexHasExtractedText: !!normalizeIndexText(extracted),
    contentIndexVersion: 2,
  };
}

function inferPhotoGroup(file) {
  const text = [file?.name, file?.subcategory, file?.phase].map(lower).join(" ");
  if (/inspection/.test(text)) return "inspection";
  if (/progress|construction/.test(text)) return "progress";
  if (/deficien/.test(text)) return "deficiency";
  if (/existing|site/.test(text)) return "site";
  return "general";
}

function confidenceBucket(score) {
  if (score >= 85) return "high";
  if (score >= 65) return "medium";
  return "low";
}

function buildClassificationConfidence(file, libraryType, drawingStage, discipline, documentClass, photoGroup, sheetCode) {
  let score = 45;
  const reasons = [];
  const name = lower(file?.name);
  const category = lower(file?.category);
  const subcategory = lower(file?.subcategory);
  const deliverableType = lower(file?.deliverableType);
  const contentType = lower(file?.contentType);

  if (libraryType === "photo") {
    if (isImageContentType(contentType) || isImageName(name)) {
      score += 35;
      reasons.push("Image file type detected");
    }
    if (category.includes("photo") || subcategory.includes("photo")) {
      score += 18;
      reasons.push("Photo category or subcategory already applied");
    }
    if (photoGroup && photoGroup !== "general") {
      score += 8;
      reasons.push(`Grouped as ${photoGroup} photo`);
    }
  }

  if (libraryType === "drawing") {
    if (isPdfName(name) || /dwg|dxf/.test(name)) {
      score += 10;
      reasons.push("Drawing-compatible file format");
    }
    if (sheetCode) {
      score += 18;
      reasons.push(`Sheet code ${sheetCode} detected`);
    }
    if (drawingStage && drawingStage !== "unspecified") {
      score += 12;
      reasons.push(`Stage ${drawingStage} detected`);
    }
    if (discipline && discipline !== "general") {
      score += 10;
      reasons.push(`${discipline} discipline detected`);
    }
    if (category.includes("drawing") || subcategory.includes("drawing") || deliverableType.includes("drawing")) {
      score += 10;
      reasons.push("Drawing category language found");
    }
  }

  if (libraryType === "document") {
    if (documentClass && documentClass !== "general") {
      score += 18;
      reasons.push(`${documentClass.replace(/_/g, " ")} class detected`);
    }
    if (isPdfName(name)) {
      score += 8;
      reasons.push("PDF document format");
    }
    if (category || subcategory) {
      score += 8;
      reasons.push("Document category metadata present");
    }
  }

  if (libraryType === "model") {
    if (/\.skp$|\.rvt$|\.ifc$|\.dwg$|\.dxf$/i.test(safeStr(file?.name))) {
      score += 30;
      reasons.push("Model file extension detected");
    }
    if (subcategory.includes("model") || subcategory.includes("3d")) {
      score += 10;
      reasons.push("3D/model subcategory detected");
    }
  }

  return {
    score: Math.min(98, score),
    level: confidenceBucket(score),
    reasons: reasons.slice(0, 4),
  };
}

function buildLibraryRouting(file, libraryType, drawingStage, documentClass, photoGroup) {
  if (libraryType === "drawing") {
    return {
      workspace: "Drawings",
      lane: drawingStage && drawingStage !== "unspecified" ? `${titleCase(drawingStage)} set` : "Drawing Set",
      nextAction: "Review stage, discipline, and permit set membership in Drawings.",
    };
  }
  if (libraryType === "photo") {
    return {
      workspace: "Photos",
      lane: photoGroup && photoGroup !== "general" ? `${titleCase(photoGroup)} photos` : "Photo Repository",
      nextAction: "Keep site and inspection images out of permit document workflows.",
    };
  }
  if (libraryType === "model") {
    return {
      workspace: "Documents",
      lane: "Models",
      nextAction: "Keep model files in Documents so Permit Studio can reference them without mixing them into sheet workflows.",
    };
  }
  return {
    workspace: "Documents",
    lane: documentClass && documentClass !== "general" ? titleCase(documentClass.replace(/_/g, " ")) : "Project Documents",
    nextAction: "Keep contracts, forms, reports, and permit support files in Documents.",
  };
}

function titleCase(value) {
  return safeStr(value)
    .split(/[_\-\s]+/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");
}

function buildSuggestionSummary(permitHints) {
  const top = Array.isArray(permitHints) ? permitHints[0] : null;
  if (!top) {
    return {
      headline: "No permit suggestion yet",
      note: "As the file gets cleaner stage, class, and scope metadata, Permit Studio can surface stronger requirement matches.",
    };
  }
  return {
    headline: `${top.stream ? top.stream.toUpperCase() : "Permit"} suggestion — ${top.title}`,
    note: top.reasons?.length ? top.reasons.slice(0, 2).join(" • ") : "Matched by file metadata and requirement overlap.",
  };
}

function buildPermitHints(file, permitPlan) {
  const bp = Array.isArray(permitPlan?.requirements?.bp) ? permitPlan.requirements.bp : [];
  const dp = Array.isArray(permitPlan?.requirements?.dp) ? permitPlan.requirements.dp : [];
  const candidates = [...bp, ...(permitPlan?.dpRequired ? dp : [])];
  if (!candidates.length) return [];

  const tokens = unique(
    [
      lower(file?.name),
      lower(file?.category),
      lower(file?.subcategory),
      lower(file?.deliverableType),
      lower(file?.libraryStage),
      lower(file?.libraryDiscipline),
      lower(file?.libraryType),
      lower(file?.documentClass),
      ...(Array.isArray(file?.matchTags) ? file.matchTags.map(lower) : []),
    ]
      .join(" ")
      .split(/[^a-z0-9]+/)
      .filter((token) => token && token.length > 2)
  );

  const hints = candidates
    .map((req) => {
      const title = lower(req?.title || req?.label);
      let score = 0;
      const reasons = [];

      if (tokens.some((token) => title.includes(token))) {
        const matched = tokens.filter((token) => title.includes(token));
        score += Math.min(35, matched.length * 10);
        reasons.push(`Title overlap: ${matched.slice(0, 3).join(", ")}`);
      }

      if (file?.libraryType === "drawing" && /plan|elevation|section|detail|site/.test(title)) {
        score += 18;
        reasons.push("Drawing-type requirement");
      }
      if (file?.libraryType === "document" && /application|form|report|letter|document|spec/.test(title)) {
        score += 15;
        reasons.push("Document-type requirement");
      }
      if (file?.libraryStage === "permit" && /permit|submission|application/.test(title)) {
        score += 20;
        reasons.push("Permit-stage match");
      }
      if (file?.libraryDiscipline === "architectural" && /architect|floor|elevation|section|site/.test(title)) {
        score += 12;
        reasons.push("Architectural discipline match");
      }

      return score > 0
        ? {
            requirementId: req?.id || req?.legacyItemId || "",
            stream: req?.stream || "",
            title: safeStr(req?.title || req?.label || "Requirement"),
            score,
            reasons,
          }
        : null;
    })
    .filter(Boolean)
    .sort((a, b) => b.score - a.score)
    .slice(0, 3);

  return hints;
}

export function buildProjectLibraryMetadata(file, permitPlan) {
  const libraryType = inferLibraryType(file);
  const sheetCode = libraryType === "drawing" ? inferSheetCode(file?.name) : "";
  const revision =
    libraryType === "drawing"
      ? safeStr(file?.revision) || inferRevision(file?.name)
      : "";
  const drawingStage = libraryType === "drawing" ? inferDrawingStage(file) : "";
  const discipline = libraryType === "drawing" ? inferDiscipline(file) : "";
  const documentClass = libraryType === "document" ? inferDocumentClass(file) : "";
  const photoGroup = libraryType === "photo" ? inferPhotoGroup(file) : "";
  const permitHints = buildPermitHints({ ...file, libraryType, libraryStage: drawingStage, libraryDiscipline: discipline, documentClass }, permitPlan);
  const classificationConfidence = buildClassificationConfidence(file, libraryType, drawingStage, discipline, documentClass, photoGroup, sheetCode);
  const routing = buildLibraryRouting(file, libraryType, drawingStage, documentClass, photoGroup);
  const suggestionSummary = buildSuggestionSummary(permitHints);
  const contentIndex = buildLibraryContentIndex(file, {
    sheetCode,
    libraryType,
    libraryStage: drawingStage,
    libraryDiscipline: discipline,
    documentClass,
  });

  return {
    manualLibraryType: manualOverride(file?.manualLibraryType || file?.libraryTypeManual) || null,
    manualLibraryDiscipline: manualOverride(file?.manualLibraryDiscipline || file?.libraryDisciplineManual) || null,
    matchTags: unique(Array.isArray(file?.matchTags) ? file.matchTags : Array.isArray(file?.contentTags) ? file.contentTags : []),
    libraryType,
    libraryStage: drawingStage,
    libraryDiscipline: discipline,
    sheetCode,
    revision,
    documentClass,
    photoGroup,
    permitHints,
    permitHintCount: permitHints.length,
    classificationConfidence,
    libraryRouting: routing,
    suggestionSummary,
    ...contentIndex,
  };
}

export function enrichProjectLibraryFile(file, permitPlan) {
  const metadata = buildProjectLibraryMetadata(file, permitPlan);
  return {
    ...file,
    ...metadata,
  };
}

function activeProjectFiles(files) {
  const list = Array.isArray(files) ? files : [];
  return list.filter((file) => lower(file?.status || "active") !== "deleted");
}

export function buildProjectLibraryWorkspaceModel(files) {
  const list = activeProjectFiles(files);
  const byType = {
    drawings: list.filter((file) => file?.libraryType === "drawing"),
    documents: list.filter((file) => file?.libraryType === "document" || file?.libraryType === "model"),
    photos: list.filter((file) => file?.libraryType === "photo"),
    permitFiles: list.filter((file) => Number(file?.permitHintCount || 0) > 0),
  };

  return {
    drawings: {
      count: byType.drawings.length,
      primaryLabel: "Drawings",
      note: "Sheet-based files organized by stage and discipline.",
      nextAction: byType.drawings.length ? "Review the current permit set and discipline grouping." : "Upload the first drawing set to start stage detection.",
    },
    documents: {
      count: byType.documents.length,
      primaryLabel: "Documents",
      note: "Contracts, reports, forms, specifications, and models.",
      nextAction: byType.documents.length ? "Confirm document classes and keep permit forms out of drawing workflows." : "Upload contracts, reports, and support files into Documents.",
    },
    photos: {
      count: byType.photos.length,
      primaryLabel: "Photos",
      note: "Site, inspection, progress, and deficiency captures.",
      nextAction: byType.photos.length ? "Check photo groups so field images stay out of permit document work." : "Upload site or inspection photos to create the first photo group.",
    },
    permitFiles: {
      count: byType.permitFiles.length,
      primaryLabel: "Permit Studio Files",
      note: "Files with at least one permit hint (overlaps Drawings/Photos; not a separate upload pile).",
      nextAction: byType.permitFiles.length ? "Open Permit Studio Files and confirm the strongest requirement links." : "Permit hints will appear here as the library gains stronger file context.",
    },
  };
}

export function summarizeProjectLibrary(files) {
  const list = activeProjectFiles(files);
  const drawings = list.filter((file) => file?.libraryType === "drawing");
  const documents = list.filter((file) => file?.libraryType === "document");
  const photos = list.filter((file) => file?.libraryType === "photo");
  const models = list.filter((file) => file?.libraryType === "model");

  return {
    total: list.length,
    drawings: drawings.length,
    documents: documents.length,
    photos: photos.length,
    models: models.length,
    permitCandidates: list.filter((file) => (file?.permitHintCount || 0) > 0).length,
    groupedPhotos: unique(photos.map((file) => file?.photoGroup)).length,
    drawingDisciplines: unique(drawings.map((file) => file?.libraryDiscipline)).length,
    documentClasses: unique(documents.map((file) => file?.documentClass)).length,
  };
}
